/* External Deps */
import { FC, ReactElement, useState, useMemo } from "react";
import { Box, InputAdornment, Stack, Typography, Dialog, DialogActions, DialogContent, DialogTitle, Button } from "@mui/material";

/* Internal Deps */
import { DynamicTable, IconImage, PageHeader } from "@/components";
import {
  HeaderRightNode,
  HeaderSearchBox,
  StyledContainedButton,
  StyledTextInput,
} from "../../../../styles";
import { columns } from "@/columns/COMMON/product-certificates";
import {
  useGetProductCertsQuery,
  useDeleteProductCertMutation,
} from "@/services";
import {
  useDebounce,
  useSearchQuery,
  useSnackbar,
} from "@/hooks";
import { search_img, upload_img } from "@/constants";
import { useAppSelector } from "@/store";
import { DocumentAccessLevel, roles } from "@/roles";

interface CertificateManagementProps {
  onUploadClick: () => void;
  onViewCertificate: (certificateId: string) => void;
  onEditCertificate: (certificateId: string) => void;
}

const CertificateManagement: FC<CertificateManagementProps> = ({
  onUploadClick,
  onViewCertificate,
  onEditCertificate,
}): ReactElement => {
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [actionLoading, setActionLoading] = useState<{
    view: string[];
    download: string[];
    delete: string[];
    edit: string[];
  }>({
    view: [],
    download: [],
    delete: [],
    edit: [],
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [certificateToDelete, setCertificateToDelete] = useState<CertificateSummary | null>(null);

  const { show } = useSnackbar();
  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const { user } = useAppSelector((state) => state.auth);
  const { updateQuery } = useSearchQuery();

  const [deleteProductCert] = useDeleteProductCertMutation();

  const canSelectClients = useMemo(() => {
    const allowedRoles = [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN] as string[];
    return allowedRoles.includes(user?.userRole ?? "");
  }, [user]);

  const isTDAdmin = useMemo(() => {
    return user?.userRole === roles.TDADMIN;
  }, [user, selectedTenant]);

  const shouldSkip = useMemo(
    () => {
      if (isTDAdmin && selectedTenant?.tenantType === "TRADEDIVE_CLIENT") {
        return false;
      }
      // Only enforce client selection for roles that can select clients
      return canSelectClients && !selectedClient;
    },
    [canSelectClients, selectedClient, selectedTenant, isTDAdmin]
  );

  // Get clientId for the query
  const clientId = useMemo(() => {
    if (isTDAdmin && selectedTenant?.tenantType === "TRADEDIVE_CLIENT") {
      return selectedTenant?._id;
    }
    if (selectedClient?._id) {
      return selectedClient._id;
    }
    // For TD Client or SP Client, default to their own tenantId
    if (user?.userRole === roles.TDCLIENT || user?.userRole === roles.SPCLIENT) {
      return user?.tenantId;
    }
    return undefined;
  }, [isTDAdmin, selectedTenant, selectedClient, user]);

  const {
    data: apiData,
    isLoading,
    isFetching,
  } = useGetProductCertsQuery(
    {
      clientId: clientId!,
      pageNo: page,
      pageSize: limit,
    },
    {
      skip: shouldSkip || !clientId,
      refetchOnMountOrArgChange: true,
    }
  );

  const canUploadCerts = useMemo(
    () =>
      user?.userRole !== roles.SPCLIENT ||
      user?.clientPermission?.documents === DocumentAccessLevel.Admin,
    [user]
  );

  const handleViewCertificate = async (cert: CertificateSummary) => {
    setActionLoading(prev => ({
      ...prev,
      view: [...prev.view, cert._id],
    }));

    try {
      onViewCertificate(cert._id);
    } finally {
      setActionLoading(prev => ({
        ...prev,
        view: prev.view.filter(id => id !== cert._id),
      }));
    }
  };

  const handleDownloadCertificate = async (cert: CertificateSummary) => {
    setActionLoading(prev => ({
      ...prev,
      download: [...prev.download, cert._id],
    }));

    try {
      // First, we need to get the full certificate data
      // For now, we'll show a message that they need to view the certificate first
      show(
        "Download Certificate",
        "Please open the certificate details to download the CUSMA/USMCA/T-MEC certificate",
        "info"
      );
      
      // Trigger view to open the detail modal where download is available
      onViewCertificate(cert._id);
    } catch (error) {
      show(
        "Download failed",
        "Failed to download the certificate file",
        "error"
      );
    } finally {
      setActionLoading(prev => ({
        ...prev,
        download: prev.download.filter(id => id !== cert._id),
      }));
    }
  };

  const handleDeleteCertificate = (cert: CertificateSummary) => {
    setCertificateToDelete(cert);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteCertificate = async () => {
    if (!certificateToDelete) return;

    setActionLoading(prev => ({
      ...prev,
      delete: [...prev.delete, certificateToDelete._id],
    }));

    try {
      const result = await deleteProductCert(certificateToDelete._id).unwrap();
      
      if (result) {
        show(
          "Certificate deleted",
          `Certificate "${certificateToDelete.fileName}" was successfully deleted`,
          "success"
        );
      }
    } catch (error) {
      show(
        "Delete failed",
        "Failed to delete the certificate. Please try again.",
        "error"
      );
    } finally {
      setActionLoading(prev => ({
        ...prev,
        delete: prev.delete.filter(id => id !== certificateToDelete._id),
      }));
      setDeleteDialogOpen(false);
      setCertificateToDelete(null);
    }
  };

  const handleEditCertificate = async (cert: CertificateSummary) => {
    setActionLoading(prev => ({
      ...prev,
      edit: [...prev.edit, cert._id],
    }));

    try {
      onEditCertificate(cert._id);
    } finally {
      setActionLoading(prev => ({
        ...prev,
        edit: prev.edit.filter(id => id !== cert._id),
      }));
    }
  };

  const handleActions = (type: "view" | "download" | "delete" | "edit", cert: CertificateSummary) => {
    switch (type) {
      case "view":
        handleViewCertificate(cert);
        break;
      case "download":
        handleDownloadCertificate(cert);
        break;
      case "delete":
        handleDeleteCertificate(cert);
        break;
      case "edit":
        handleEditCertificate(cert);
        break;
    }
  };

  const filterCertificates = useDebounce<string>((search) => {
    updateQuery({ search });
    setPage(1);
  }, 1200);

  const { data = [], meta } = apiData ?? {};
  const countCerts = meta?.countDocs ?? 0;

  if (shouldSkip || !clientId) {
    return (
      <Stack sx={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
        <Typography variant="h6" color="text.secondary">
          Please select a client to view certificates
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack sx={{ flex: 1 }}>
      <PageHeader
        title={"Certificate Management"}
        rightNode={
          <HeaderRightNode gap={3}>
            <HeaderSearchBox>
              <StyledTextInput
                type="text"
                placeholder="Search certificates..."
                handleChangeValue={filterCertificates}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconImage src={search_img} size="auto" />
                    </InputAdornment>
                  ),
                }}
              />
            </HeaderSearchBox>
            {canUploadCerts && (
              <Box>
                <StyledContainedButton
                  startIcon={<IconImage src={upload_img} size="auto" />}
                  size="large"
                  title="Upload Shipment"
                  onClick={onUploadClick}
                />
              </Box>
            )}
          </HeaderRightNode>
        }
      />

      <DynamicTable
        data={data}
        stickyHeader
        hideScrollBars
        columns={columns({
          action: handleActions,
          actionsLoading: actionLoading,
        })}
        sx={{
          flex: 1,
          "& .MuiTable-root": {
            borderCollapse: "collapse",
          },
          "& .MuiTableRow-root:last-of-type .MuiTableCell-root": {
            borderBottom: "1px solid #e6e6e9 !important",
          },
        }}
        isLoading={isLoading || isFetching}
        handlePageChange={setPage}
        handleLimitChange={setLimit}
        limit={limit}
        page={page}
        total={countCerts}
        hidePagination={false}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Certificate</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the certificate "{certificateToDelete?.fileName}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={actionLoading.delete.includes(certificateToDelete?._id || "")}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteCertificate}
            color="error"
            variant="contained"
            disabled={actionLoading.delete.includes(certificateToDelete?._id || "")}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default CertificateManagement;